/**
 * ReportCodeSheet — driver flow for reporting a bad access code.
 *
 * Phase 2 (Gate Codes) — P2.4b. Opens as a stacked sheet on top of the
 * Update Status (DeliveryConfirmationSheet) modal. When dismissed, the
 * underlying POD form is preserved.
 *
 * Multi-code: when the stop has >1 active code, a radio picker appears.
 * Single-code: picker is hidden and that code is auto-selected.
 *
 * Submit uses an explicit report command. Saving the report and attempting
 * notification are distinct outcomes; neither proves a dispatcher read it.
 *
 * Props:
 *   open:              bool
 *   onOpenChange:      (next) => void
 *   codes:             AccessCodeLookupResult[] (the lookup result for this stop)
 *   reportedCodeIds:   Set<string> — codes already reported in this session
 *   orderId:           string
 *   onReported:        (codeId) => void — fires after acknowledged acceptance
 */
import React, { useState, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { reportAccessCode } from '@/api/accessCodes';
import { getActiveAuthToken, getActiveSessionRevision, getActiveSessionType, subscribeActiveSessionRevision, SESSION_TYPE_CLOUD } from '@/api/authenticatedClient';

export default function ReportCodeSheet({
  open,
  onOpenChange,
  codes,
  reportedCodeIds,
  orderId,
  onReported,
  openingId,
  disabled = false,
}) {
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  // A replacement login does not grant an already-open report new authority.
  const opening = useMemo(() => ({
    revision: getActiveSessionRevision(), token: getActiveAuthToken(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [open, orderId, openingId]);
  const latest = useRef(null);
  latest.current = { opening, open, disabled };
  const mounted = useRef(false);
  const inFlight = useRef(null);
  const sameSession = () => opening.revision === getActiveSessionRevision()
    && !!opening.token && opening.token === getActiveAuthToken()
    && getActiveSessionType() === SESSION_TYPE_CLOUD;
  const owns = () => mounted.current && latest.current.open && !latest.current.disabled
    && latest.current.opening === opening && sameSession();
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  // Memoize so the form-reset effect doesn't fire on every render.
  // Without this, selectedCodeId would be reset to '' immediately after the
  // user picks a radio, because `codes.filter(...)` is a fresh array each render.
  const reportableCodes = useMemo(
    () => codes.filter((c) => !reportedCodeIds.has(c.id)),
    [codes, reportedCodeIds],
  );
  const single = reportableCodes.length === 1;

  const [selectedCodeId, setSelectedCodeId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState('');

  // Reset only for a new opening/order, never merely because a lookup result
  // array changed. The previous request cannot release this opening's lock.
  const initialized = useRef(null);
  useEffect(() => {
    if (open && initialized.current !== opening) {
      initialized.current = opening;
      setSelectedCodeId(single ? reportableCodes[0].id : '');
      setReason('');
      setSubmitting(false);
      setOutcome('');
      inFlight.current = null;
    }
  }, [open, opening, single, reportableCodes]);

  const currentSession = revision === opening.revision && sameSession();
  const canSubmit = reportableCodes.some((code) => code.id === selectedCodeId)
    && reason.trim().length > 0 && !submitting && !disabled && currentSession;

  const handleSubmit = async () => {
    if (!canSubmit || !owns() || inFlight.current === opening) return;
    inFlight.current = opening;
    setSubmitting(true);
    setOutcome('');
    let result;
    try {
      result = await reportAccessCode(
        selectedCodeId, { reason: reason.trim(), ...(orderId ? { order_id: orderId } : {}) }, opening,
      );
      if (result?.report_status !== 'accepted' || result?.access_code?.id !== selectedCodeId
          || result.access_code.deprecated !== true) throw new Error('Unconfirmed report response');
    } catch (error) {
      if (!owns()) return;
      const status = error?.response?.status;
      const refused = [400, 401, 403, 404, 422].includes(status) || error?.code === 'REPORT_SESSION_CHANGED';
      setOutcome(refused
        ? 'Report not accepted. Check your permissions and report details before trying again.'
        : 'Report acceptance could not be confirmed. Your text is retained. Check the code before retrying; a manual retry may repeat a notification.');
      inFlight.current = null;
      setSubmitting(false);
      return;
    }
    // Shared-data invalidation survives closing this form, but never a session
    // replacement. Form actions below belong only to the submitting opening.
    if (sameSession()) {
      window.dispatchEvent(
        new CustomEvent('access-code-deprecated', { detail: { codeId: selectedCodeId, sessionRevision: opening.revision } }),
      );
    }
    if (!owns()) return;
    const message = result.notification_status === 'attempt_completed'
      ? 'Code reported.' : 'Code reported. Dispatch notification could not be confirmed.';
    setOutcome(message);
    toast.success(message);
    // Consumer failures cannot turn acknowledged acceptance into retry advice.
    try { onReported(selectedCodeId); } catch { /* accepted; retain disabled state */ }
    if (owns()) {
      try { onOpenChange(false); } catch { /* accepted; do not resubmit */ }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-auto max-h-[90vh] overflow-y-auto rounded-t-2xl"
        data-testid="report-code-sheet"
      >
        <SheetHeader>
          <SheetTitle>Report a code</SheetTitle>
          <SheetDescription>
            Tell dispatch what went wrong. The code will be hidden from future
            deliveries.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {!currentSession && <p role="status">Reopen this report from your current online session.</p>}
          {outcome && <p role="status">{outcome}</p>}
          {!single && (
            <div>
              <div className="text-sm font-medium text-stone-700 mb-2">
                Which code?
              </div>
              <RadioGroup
                value={selectedCodeId}
                onValueChange={setSelectedCodeId}
                data-testid="report-code-picker"
              >
                {codes.map((c) => {
                  const alreadyReported = reportedCodeIds.has(c.id);
                  return (
                    <div
                      key={c.id}
                      className={`flex items-center gap-3 py-1 ${alreadyReported ? 'opacity-50' : ''}`}
                    >
                      <RadioGroupItem
                        value={c.id}
                        id={`report-code-radio-${c.id}`}
                        disabled={alreadyReported || submitting || !currentSession || disabled}
                      />
                      <Label
                        htmlFor={`report-code-radio-${c.id}`}
                        className="text-sm font-normal flex-1"
                      >
                        <span className="font-mono font-bold">{c.code}</span>
                        {c.instructions && (
                          <span className="text-stone-500 ml-2">— {c.instructions}</span>
                        )}
                        {alreadyReported && (
                          <span className="text-xs text-stone-400 ml-2">(already reported)</span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          )}

          <div>
            <Textarea
              placeholder="Issue?"
              value={reason}
              disabled={submitting || !currentSession || disabled}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              data-testid="report-code-reason"
              autoFocus
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="report-code-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="bg-red-600 hover:bg-red-700"
              data-testid="report-code-submit"
            >
              {submitting ? 'Submitting…' : 'Submit report'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
